import { Injectable, signal, effect } from '@angular/core';

export type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly THEME_KEY = 'app-theme';

  // Signal para el tema actual
  currentTheme = signal<Theme>(this.getInitialTheme());

  constructor() {
    // Aplicar el tema inmediatamente al cargar el servicio
    this.applyTheme(this.currentTheme());

    // Efecto para aplicar el tema cuando cambie
    effect(() => {
      this.applyTheme(this.currentTheme());
    });
  }

  /**
   * Alternar entre modo claro y oscuro
   */
  toggleTheme(): void {
    const newTheme = this.currentTheme() === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Establecer un tema específico
   */
  setTheme(theme: Theme): void {
    this.currentTheme.set(theme);
    localStorage.setItem(this.THEME_KEY, theme);
  }

  /**
   * Obtener el tema inicial desde localStorage o preferencias del sistema
   */
  private getInitialTheme(): Theme {
    // Intentar cargar desde localStorage
    const savedTheme = localStorage.getItem(this.THEME_KEY) as Theme;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }

    // Si no hay tema guardado, usar preferencias del sistema
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
      return prefersDark ? 'dark' : 'light';
    }

    // Fallback a tema claro
    return 'light';
  }

  /**
   * Aplicar el tema al DOM
   */
  private applyTheme(theme: Theme): void {
    if (typeof document === 'undefined') return;

    const html = document.documentElement;

    // Remover clases previas
    html.classList.remove('light-theme', 'dark-theme');

    // Agregar nueva clase
    html.classList.add(`${theme}-theme`);

    // Establecer color-scheme para elementos nativos del navegador
    html.style.colorScheme = theme;
  }
}
